import { ILocationPayload, useLocation } from '@fareye/common'
import {
  BaseLayer,
  CircleMarker,
  L,
  MapView,
  Marker,
  Polyline,
  Tooltip,
  latLng
} from '@fareye/ui'
import { FaMapMarkerAlt, isEmpty } from '@fareye/utils'
import React from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { ExceptionMarker } from '../../../../components/Marker/ExceptionMarker'
import { TripPoiMarker } from '../../../../components/Marker/TripPoiMarker'
import { Exception, GPSLocation, TripPoiProps } from '../../../../utility/types'
import { CurrentLocationMarker } from '../CurrentLocationMarker'
import { HistoryMarker } from './../../../../components/Marker/HistoryMarker'
import { OverspeedMarker } from './../../../../components/Marker/OverspeedMarker'
import { StoppageMarker } from './../../../../components/Marker/StoppageMarker'

const initCenter = latLng(0, 0)
const initZoom = 5

export interface TripMapProps {
  trip: any
  mapKey: string
  location: GPSLocation
  stoppages: any
  histories: any
  idealPath: any
  exceptions: Exception[]
  selectedIds: any
  defaultBaseLayer?: BaseLayer
  overspeedInstances: any
  tripPois: TripPoiProps[]
  moveMarker?: boolean
  aerisLayer?: { id: string; secret: string }
}

function TripMap({
  trip,
  mapKey,
  location,
  histories,
  stoppages,
  idealPath,
  exceptions,
  selectedIds,
  defaultBaseLayer,
  overspeedInstances,
  tripPois = [],
  moveMarker,
  aerisLayer
}: TripMapProps) {
  const [center, setCenter] = React.useState(initCenter)
  const [bounds, setBounds] = React.useState<any>(null)
  const [latLngObject, setLatLngObject] = React.useState({
    latLng: { lat: location?.latitude, lng: location?.longitude },
    latLngIndex: 0
  })

  React.useEffect(() => {
    useLocation(({ data, success }: ILocationPayload) => {
      if (success) {
        setCenter(latLng(data.coords.latitude, data.coords.longitude))
      }
    })
  }, [])

  const validHistories = React.useMemo(
    () =>
      (histories || []).filter(
        (h: any) =>
          h.latitude != null &&
          h.longitude != null &&
          isFinite(Number(h.latitude)) &&
          isFinite(Number(h.longitude))
      ),
    [histories]
  )

  React.useEffect(() => {
    if (!isEmpty(validHistories)) {
      const points: any[] = validHistories.map((location: GPSLocation) =>
        L.latLng(Number(location.latitude), Number(location.longitude))
      )
      if (trip?.coords?.origin?.lat && trip?.coords?.origin?.lng) {
        points.push(L.latLng(Number(trip.coords.origin.lat), Number(trip.coords.origin.lng)))
      }
      setBounds(L.latLngBounds(points))
    }
  }, [validHistories])

  const iconMarkup = (color = '#f3f3f3') =>
    renderToStaticMarkup(<FaMapMarkerAlt size={25} style={{ color }} />)

  const customMarkerIcon = (color: string) =>
    L.divIcon({
      html: iconMarkup(color),
      className: 'custom-marker',
      iconAnchor: [13, 25],
      iconSize: [20, 20]
    })
  return (
    <MapView
      leafletMapConfig={{
        ...(!bounds ? { center: center } : {}),
        zoom: initZoom,
        bounds
      }}
      style={{
        height: '100%',
        width: '100%'
      }}
      mapKey={mapKey}
      aerisLayer={aerisLayer}
      defaultBaseLayer={defaultBaseLayer}
    >
      <CurrentLocationMarker
        moveMarker={moveMarker}
        histories={histories}
        location={location}
        trip={trip}
      />
      {trip && (trip as any).coords && (
        <>
          {(trip as any).coords.origin &&
            isEmpty(
              Object.values((trip as any).coords.origin).filter(x => !x)
            ) && (
              <Marker
                position={L.latLng(
                  (trip as any).coords.origin.lat,
                  (trip as any).coords.origin.lng
                )}
                icon={customMarkerIcon('green')}
              >
                <Tooltip direction="top" permanent offset={[-5, -20]}>
                  <strong>Origin</strong> : {(trip as any).origin}
                </Tooltip>
              </Marker>
            )}
          {(trip as any).coords.destination &&
            isEmpty(
              Object.values((trip as any).coords.destination).filter(x => !x)
            ) && (
              <Marker
                position={L.latLng(
                  (trip as any).coords.destination.lat,
                  (trip as any).coords.destination.lng
                )}
                icon={customMarkerIcon('red')}
              >
                <Tooltip direction="top" permanent offset={[-5, -20]}>
                  <strong>Destination</strong> : {(trip as any).destination}
                </Tooltip>
              </Marker>
            )}
        </>
      )}
      {trip?.probableStoppageLatitude && trip?.probableStoppageLongitude ? (
        <Marker
          position={L.latLng(
            trip.probableStoppageLatitude,
            trip.probableStoppageLongitude
          )}
          icon={customMarkerIcon('blue')}
        >
          <Tooltip direction="bottom" permanent offset={[-5, -20]}>
            <strong>Identified Unloading Point</strong> :{' '}
            {trip.probableStoppageLandmark}
          </Tooltip>
        </Marker>
      ) : null}
      {exceptions &&
        exceptions.map((exception: any, index: number) => (
          <ExceptionMarker
            exception={exception}
            selected={selectedIds.exceptionId === exception.id}
            key={`exceptionMarker_${index}`}
          />
        ))}
      {idealPath && (
        <>
          {idealPath.map((path: any, index: number) => {
            return (
              <CircleMarker
                key={`path${index}`}
                radius={1}
                center={latLng(path.latitude, path.longitude)}
                color="#EF6C00"
              />
            )
          })}
        </>
      )}
      {!isEmpty(validHistories) && (
        <>
          {validHistories.map((history: any, index: number) => (
            <HistoryMarker
              iconUrl="https://i.imgur.com/WK4MLHw.png"
              iconSize={[5, 5]}
              key={`historyMarker_${index}`}
              history={history}
              selected={
                selectedIds.locationId ===
                `${history.vehicleNo}${history.istTimestamp}`
              }
            />
          ))}
          <Polyline
            positions={validHistories.map((history: any) =>
              L.latLng(Number(history.latitude), Number(history.longitude))
            )}
          />
        </>
      )}
      {overspeedInstances &&
        overspeedInstances.map((instance: any, index: number) => (
          <OverspeedMarker
            iconUrl="https://i.imgur.com/kquTnXN.png"
            iconSize={[20, 20]}
            overspeedInstance={instance}
            key={`overspeedMarkers_${index}`}
            selected={selectedIds.overspeedInstanceId === instance.id}
          />
        ))}
      {stoppages &&
        stoppages.map((stoppage: any, index: number) => (
          <StoppageMarker
            iconUrl="https://i.imgur.com/ZmdktGt.png"
            iconSize={[20, 20]}
            key={`stoppageMarker_${index}`}
            stoppage={stoppage}
            selected={selectedIds.stoppageId === stoppage.id}
          />
        ))}
      {tripPois?.length ? (
        <>
          {tripPois.map(poi => (
            <TripPoiMarker
              {...poi}
              key={`poi_${poi.poiNickName}_${poi.poiType}_${poi.radius}`}
            />
          ))}
        </>
      ) : null}
    </MapView>
  )
}

export default TripMap
